/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ptBR } from 'date-fns/locale/pt-BR';
import { CalendarBlank, CaretDown, CaretUp } from 'phosphor-react';
import { Calendar } from './Calendar';

export interface Period {
  from: Date;
  to: Date;
  label: string;
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysAgo = (days: number) => {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() - days);
  return d;
};

const today = () => startOfDay(new Date());

export const periodPresets: Period[] = [
  { label: 'Últimos 7 dias', from: daysAgo(6), to: today() },
  { label: 'Últimos 14 dias', from: daysAgo(13), to: today() },
  { label: 'Últimos 30 dias', from: daysAgo(29), to: today() },
  { label: 'Últimos 90 dias', from: daysAgo(89), to: today() },
  {
    label: 'Este mês',
    from: new Date(today().getFullYear(), today().getMonth(), 1),
    to: today(),
  },
  {
    label: 'Mês passado',
    from: new Date(today().getFullYear(), today().getMonth() - 1, 1),
    to: new Date(today().getFullYear(), today().getMonth(), 0),
  },
];

export const defaultPeriod: Period = periodPresets[2];

const formatRange = (from: Date, to: Date) => {
  const fmt = (d: Date) =>
    d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '');
  return `${fmt(from)} – ${fmt(to)}`;
};

export const periodDays = (period: Period) =>
  Math.round((startOfDay(period.to).getTime() - startOfDay(period.from).getTime()) / 86400000) + 1;

interface PeriodSelectProps {
  value: Period;
  onChange: (period: Period) => void;
}

export const PeriodSelect = ({ value, onChange }: PeriodSelectProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [draft, setDraft] = useState<[Date | null, Date | null]>([value.from, value.to]);

  const applyPreset = (preset: Period) => {
    onChange(preset);
    setDraft([preset.from, preset.to]);
    onClose();
  };

  const applyRange = (range: [Date | null, Date | null]) => {
    setDraft(range);
    const [from, to] = range;
    if (from && to) {
      onChange({ from: startOfDay(from), to: startOfDay(to), label: formatRange(from, to) });
    }
  };

  const handleOpen = () => {
    setDraft([value.from, value.to]);
    onOpen();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
      autoFocus={false}
    >
      <PopoverTrigger>
        <Flex
          as="button"
          type="button"
          aria-label="Período"
          h="34px"
          px="10px"
          align="center"
          gap="8px"
          bg="surface.base"
          border="1px solid"
          borderColor="line.input"
          borderRadius="lg"
          boxShadow="control"
          cursor="pointer"
          fontSize="13px"
          color="content.strong"
          _hover={{ borderColor: '#DCDCDC' }}
          _focusVisible={{ borderColor: 'line.ring', boxShadow: 'outline', outline: 'none' }}
          _expanded={{ bg: 'surface.selected' }}
        >
          <Icon as={CalendarBlank} w="14px" h="14px" color="content.subtle" />
          <Text as="span" whiteSpace="nowrap">
            {value.label}
          </Text>
          <Box
            as="span"
            display="flex"
            flexDirection="column"
            alignItems="center"
            color="content.strong"
            opacity={0.8}
            ml="2px"
            sx={{ '& > svg + svg': { mt: '-8px' } }}
          >
            <CaretUp size={14} />
            <CaretDown size={14} />
          </Box>
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="auto" p={0}>
        <PopoverBody p={0}>
          <Flex>
            <Flex
              direction="column"
              gap="2px"
              p="8px"
              borderRight="1px solid"
              borderColor="line.hairline"
              minW="164px"
            >
              {periodPresets.map((preset) => {
                const active = preset.label === value.label;
                return (
                  <Button
                    key={preset.label}
                    type="button"
                    variant="ghost"
                    size="sm"
                    justifyContent="flex-start"
                    borderRadius="sm"
                    h="32px"
                    px="8px"
                    fontWeight={active ? 500 : 400}
                    color="content.strong"
                    bg={active ? 'surface.selected' : 'transparent'}
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </Flex>
            <Box p="12px">
              <Calendar
                mode="range"
                locale={ptBR}
                selected={{
                  ...(draft[0] ? { from: draft[0] } : { from: undefined }),
                  ...(draft[1] ? { to: draft[1] } : {}),
                }}
                disabled={{ after: today() }}
                endMonth={today()}
                defaultMonth={draft[0] ?? value.from}
                onSelect={(range: { from?: Date; to?: Date } | undefined) =>
                  applyRange([range?.from ?? null, range?.to ?? null])
                }
              />
            </Box>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
